/**
 * level3Session.ts
 * Per-level session helpers for World 3 — Brasscloud Foundry (greybox).
 *
 * Thin config layer over {@link createWorldSession}. Four main stages
 * plus the King Rivet boss arena (3-B) and a secret stage (3-0). The 3-B
 * completion path persists the wall-slide ability unlock so World 4
 * becomes selectable on the world map (the W4 unlock predicate in
 * `worldUnlock.ts` keys on `levels['3-B'].completed`).
 *
 * The named exports below preserve the original public surface so the
 * dispatcher and tests are untouched; the shared bodies live in
 * `worldSessionFactory.ts`.
 *
 * @module level3Session
 */

import {
  createWorldSession,
  type WorldSession,
  type WorldSessionConfig,
} from './worldSessionFactory';
import type { PlayScene } from '../scenes/PlayScene';
import { persistWallSlideUnlock } from '../services/abilityUnlocks';

/** Shape returned by {@link getWorld3Session} — alias of the shared bundle. */
export type World3Session = WorldSession;

// DECISION: the id→number and next-chain maps stay explicit so the
// factory's hasOwnProperty guards keep rejecting prototype keys. 3-B →
// integer 5 (boss arena), secret stage 3-0 → integer 0, matching W1's
// convention so `getLevel(3, 0)` resolves and the shard-gate counter
// (`countCollectedShards`) correctly excludes it.
const CONFIG: WorldSessionConfig = {
  worldId: 3,
  worldLabel: 'World 3',
  idToLevelNumber: {
    '3-1': 1,
    '3-2': 2,
    '3-3': 3,
    '3-4': 4,
    '3-B': 5,
    '3-0': 0,
  },
  fallbackId: '3-1',
  nextChain: {
    '3-1': '3-2',
    '3-2': '3-3',
    '3-3': '3-4',
    '3-4': '3-B',
    // 3-B and the secret stage 3-0 are absent → resolve to `null` (bounce
    // back to the world map).
  },
  // DECISION: lazy `() => persistWallSlideUnlock()` so the W3 unlock spy in
  // the session tests still intercepts the call (resolves the namespace
  // binding at call time, not config-build time). Mirrors the W4 wiring.
  abilityUnlock: { bossId: '3-B', persist: () => persistWallSlideUnlock() },
};

const helpers = createWorldSession(CONFIG);

/**
 * Resolve a `?level=` query value to a canonical World 3 session id.
 * Falls back to `'3-1'` for any unknown or missing input.
 *
 * @param raw - Raw param value from the router.
 * @returns A valid session id string.
 */
export function resolveWorld3LevelId(raw: string | string[] | undefined): string {
  return helpers.resolveLevelId(raw);
}

/**
 * Build a session bundle for the given World 3 level id.
 *
 * @param levelId - Canonical id (`'3-1'`..`'3-4'`, `'3-B'`, or `'3-0'` secret).
 * @returns A World3Session with everything `scene.init` needs.
 * @throws If the level id is not registered.
 */
export function getWorld3Session(levelId: string): World3Session {
  return helpers.getSession(levelId);
}

/**
 * Initialise the PlayScene from a World 3 session bundle. No finale
 * wiring — W3 stages exit on right-edge collision.
 *
 * @param scene - The PlayScene to initialise.
 * @param session - The session bundle returned by {@link getWorld3Session}.
 * @param skipCheckpointRestore - When true, respawn at the level start.
 */
export function initWorld3Scene(
  scene: PlayScene,
  session: World3Session,
  skipCheckpointRestore = false,
): void {
  helpers.initScene(scene, session, skipCheckpointRestore);
}

/**
 * Return the next World 3 level id in normal-progression order, or
 * `null` if the player is at the last shipped stage.
 *
 * @param levelId - Current level id.
 * @returns The next level id, or `null` if there is none.
 */
export function getNextWorld3LevelId(levelId: string): string | null {
  return helpers.getNextLevelId(levelId);
}

/**
 * Persist the just-completed World 3 level's collected ids and score.
 * Fire-and-forget: failures are logged, never thrown. When the boss
 * arena (`'3-B'`) is the completed level, also fires {@link persistWallSlideUnlock}
 * (snapshot-before-await race guard lives in the factory).
 *
 * @param levelId - Save key for the completed level.
 */
export async function persistWorld3Completion(levelId: string): Promise<void> {
  await helpers.persistCompletion(levelId);
}
